<script setup lang="ts">
import { required as veeRequiredValidator } from '@vee-validate/rules'
import { defineRule, Field as VeeField } from 'vee-validate'

import { useAddressesStore } from '@/store/addresses/addresses'
import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'
import { useToggleStore } from '@/store/toggle/toggle'

import { DeliveryMethods } from '@/api/modules/basket/types'
import { Success } from '@/api/results'

import BasketAddressList from '@/components/Basket/basket-addresses/BasketAddressList.vue'

import type { AddressClient } from '@/api/modules/addresses/addresses.types'

const BasketFailed = defineAsyncComponent(
  () => import('@/components/Basket/basket-utils/BasketFailed.vue'),
)
const DialogBasketAddAddressForm = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketAddAddressForm.vue'),
)
const NewDialogBasketAddAddressForm = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/NewDialogBasketAddAddressForm.vue'),
)
const NewDialogBasketEditAddress = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/NewDialogBasketEditAddress.vue'),
)
const NewDialogBasketDeleteAddress = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/NewDialogBasketDeleteAddress.vue'),
)
const DialogBasketEditAddressForm = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketEditAddressForm.vue'),
)
const DialogBasketDeleteAddress = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketDeleteAddress.vue'),
)
const DialogRedirectOtherCity = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogRedirectOtherCity.vue'),
)
const BasketDeliveryAddressNotification = defineAsyncComponent(
  () =>
    import(
      '@/components/Basket/basket-addresses/BasketDeliveryAddressNotification.vue'
    ),
)

type Props = {
  selectedAddress?: AddressClient
}
defineProps<Props>()

const { showSnackbar } = useAppStore()
const basketStore = useBasketStore()
const addressesStore = useAddressesStore()
const { newFetchingApiEnabled } = useToggleStore()

const emit = defineEmits(['delivery-method-changed', 'select-delivery-courier'])

const dialogs = ref({
  addAddress: {
    visible: false,
  },
  editAddress: {
    visible: false,
    editableAddress: null as AddressClient | null,
  },
  deleteAddress: {
    visible: false,
    address: null as AddressClient | null,
  },
  redirectCity: {
    visible: false,
    address: null as AddressClient | null,
  },
  isShowNotification: false,
})

const basket = computed(() => basketStore.data)

const addresses = computed(() => addressesStore.addresses)

const deliveryCost = computed(() => basket.value.delivery.cost)

const setSelectedAddress = async (value: AddressClient) => {
  if (!value || ('is_in_delivery_area' in value && !value.is_in_delivery_area))
    return

  await basketStore.setDeliveryMethod({
    address_id: value.id,
    delivery_method: DeliveryMethods.COURIER,
  })

  onDeliveryMethodChanged()
}

const openAddAddressDialog = () => {
  dialogs.value.addAddress.visible = true
}

const openEditAddressDialog = (address: AddressClient) => {
  dialogs.value.editAddress.visible = true
  dialogs.value.editAddress.editableAddress = address
}

const closeEditAddressDialog = () => {
  dialogs.value.editAddress.visible = false
  dialogs.value.editAddress.editableAddress = null
}

const openDeleteAddressDialog = (address: AddressClient) => {
  dialogs.value.deleteAddress.visible = true
  dialogs.value.deleteAddress.address = address
}

const openDialogRedirectCity = (address: AddressClient) => {
  dialogs.value.redirectCity.visible = true
  dialogs.value.redirectCity.address = address
}

const openDialogNotification = () => {
  dialogs.value.isShowNotification = true
}

const closeDialogRedirectCity = () => {
  dialogs.value.redirectCity.visible = false
}

const onDeliveryMethodChanged = () => {
  emit('delivery-method-changed')
}

const onSelectDeliveryCourier = () => {
  emit('select-delivery-courier')
}

const addAddress = async (address: AddressClient) => {
  const { allow_redirect, is_in_delivery_area, id } = address

  // Если адрес в зоне доставки, то не устанавливаем его
  if (is_in_delivery_area && !newFetchingApiEnabled) {
    await basketStore.setDeliveryMethod({
      delivery_method: DeliveryMethods.COURIER,
      address_id: id,
    })
  }

  // В случае если адрес в зоне доставки и адрес текущего города
  const isAllowedAddress =
    is_in_delivery_area && newFetchingApiEnabled && !allow_redirect

  if (isAllowedAddress) {
    await basketStore.setDeliveryMethod({
      delivery_method: DeliveryMethods.COURIER,
      address_id: id,
    })
  }

  onDeliveryMethodChanged()

  dialogs.value.addAddress.visible = false

  await fetchAddress()

  showSnackbar({
    message: 'Адрес добавлен',
    type: SnackbarType.SUCCESS,
  })

  // Если адрес вне зоны доставки
  if (!is_in_delivery_area) {
    openDialogNotification()

    return
  }

  // Если адрес в зоне доставки и другой город
  if (newFetchingApiEnabled && allow_redirect) {
    openDialogRedirectCity(address)

    return
  }

  basketStore.setAddress({
    type: DeliveryMethods.COURIER,
    id,
  })
}

const fetchAddress = async () => {
  const result = await addressesStore.getAddresses()

  if (result instanceof Success) return

  showSnackbar({ message: 'Не удалось загрузить список адресов' })
}

const editAddress = async () => {
  dialogs.value.editAddress.visible = false

  await fetchAddress()
}

const closeDialogDeleteAddress = async (
  isUpdate: boolean,
  addressId?: number,
) => {
  dialogs.value.deleteAddress.visible = false

  // В случае, если адреса не нужно обновлять или нет id
  if (!isUpdate || !addressId) return

  await fetchAddress()

  if (basket.value.delivery.addresses.courier === addressId) {
    await basketStore.fetchBasket()
  }
}

const deleteAddress = async (addressId: number) => {
  dialogs.value.editAddress.visible = false

  await fetchAddress()

  if (basket.value.delivery.addresses.courier === addressId) {
    await basketStore.fetchBasket()
  }
}

const requiredAddress = (value: string) => {
  if (!veeRequiredValidator(value)) {
    return 'Выберите адрес'
  }

  return true
}

defineRule('required-address', requiredAddress)
</script>

<template>
  <div id="address_id" class="basket-courier">
    <the-loader
      v-if="addresses.isLoading"
      size="100"
      variant="circular"
      color="var(--ui-primary)"
    />

    <basket-failed
      v-else-if="addresses.error"
      :error-message="'Не удалось загрузить список адресов'"
      @update="fetchAddress"
    />

    <div v-else>
      <div
        v-if="addresses.data.length === 0"
        class="basket-courier__address-message"
      >
        Укажите адрес для доставки
      </div>

      <vee-field
        v-slot="{ errors, field }"
        rules="required-address"
        name="address_id"
        :model-value="selectedAddress"
        validate-on-mount
      >
        <basket-address-list
          v-if="addresses.data.length"
          class="basket-courier__address-list"
          v-bind="field"
          :model-value="selectedAddress"
          :max-count-of-visible-addresses="5"
          :addresses="addresses.data"
          :delivery-cost="deliveryCost"
          @delivery-method-changed="onDeliveryMethodChanged"
          @select-delivery-courier="onSelectDeliveryCourier"
          @open-edit-address-dialog="openEditAddressDialog"
          @open-delete-address-dialog="openDeleteAddressDialog"
          @open-dialog-notification="openDialogNotification"
          @open-dialog-redirect-city="openDialogRedirectCity"
          @update:model-value="setSelectedAddress"
        />

        <the-button
          class="basket-courier__btn-add"
          icon="add-circle"
          indent-left
          variant="transparent"
          size="sm"
          font="sm"
          :ripple="false"
          @click.prevent="openAddAddressDialog"
        >
          Добавить адрес
        </the-button>

        <basket-failed
          v-if="Object.keys(errors).length"
          :error-message="errors[0]"
          without-button
        />

        <basket-failed
          v-else-if="deliveryCost && deliveryCost.error_message"
          :error-message="deliveryCost.error_message"
          without-button
        />

        <div
          v-else-if="deliveryCost && deliveryCost.help_message"
          class="basket-courier__help-message"
        >
          <nuxt-img src="/winking-face.png" alt="winking face" />
          {{ deliveryCost.help_message }}
        </div>
      </vee-field>
    </div>

    <the-dialog v-model="dialogs.editAddress.visible" :max-width="560">
      <template v-if="dialogs.editAddress.editableAddress">
        <transition v-if="!newFetchingApiEnabled" :duration="150">
          <dialog-basket-edit-address-form
            :address="dialogs.editAddress.editableAddress"
            @delete="deleteAddress"
            @edit="editAddress"
          />
        </transition>

        <transition v-if="newFetchingApiEnabled" :duration="150">
          <new-dialog-basket-edit-address
            :address="dialogs.editAddress.editableAddress"
            @delete="deleteAddress"
            @edit="editAddress"
            @close="closeEditAddressDialog"
          />
        </transition>
      </template>
    </the-dialog>

    <the-dialog v-model="dialogs.addAddress.visible" :max-width="560">
      <new-dialog-basket-add-address-form
        v-if="newFetchingApiEnabled"
        @add="addAddress"
      />

      <dialog-basket-add-address-form v-else @add="addAddress" />
    </the-dialog>

    <the-dialog v-model="dialogs.deleteAddress.visible">
      <template v-if="dialogs.deleteAddress.address">
        <new-dialog-basket-delete-address
          v-if="newFetchingApiEnabled"
          :address="dialogs.deleteAddress.address"
          @close-dialog-delete-address="closeDialogDeleteAddress"
        />

        <dialog-basket-delete-address
          v-else
          :address="dialogs.deleteAddress.address"
          @close-dialog-delete-address="closeDialogDeleteAddress"
        />
      </template>
    </the-dialog>

    <the-dialog
      v-if="newFetchingApiEnabled"
      v-model="dialogs.redirectCity.visible"
      :max-width="620"
    >
      <dialog-redirect-other-city
        v-if="dialogs.redirectCity.address"
        :address="dialogs.redirectCity.address"
        @close-dialog-delete-address="closeDialogRedirectCity"
        @close-dialog-redirect-city="closeDialogRedirectCity"
      />
    </the-dialog>

    <the-dialog
      v-model="dialogs.isShowNotification"
      :max-width="620"
      padding="0"
    >
      <basket-delivery-address-notification
        @select-delivery-courier="onSelectDeliveryCourier"
      />
    </the-dialog>
  </div>
</template>

<style lang="scss" scoped>
.basket-courier {
  .app-loader {
    position: relative;
  }

  &__address-message {
    margin-bottom: 24px;
    font-size: 18px;
    font-weight: 800;
  }

  &__address-list {
    margin-bottom: 10px;
  }

  &__help-message {
    display: flex;
    margin-top: 33px;
    font-size: 14px;
    font-weight: 600;

    img {
      align-self: flex-start;
      width: 24px;
      margin-right: 12px;
    }
  }

  &__dialog-title {
    font-size: 20px;
    font-weight: 800;
  }

  &__btn-add {
    padding-left: 32px;

    :deep(.app-button__icon) {
      left: 0px;
    }

    &:hover {
      background-color: transparent !important;
    }
  }
}
</style>
